import React, { useState } from 'react';
import { List, useTable, EditButton, ShowButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Tag, Grid, Input, Row, Col } from 'antd';
import { IResourceComponentsProps, BaseRecord, CrudFilters } from '@refinedev/core';
import { describeIosPrice } from './iosPrice';

const { useBreakpoint } = Grid;

const CATEGORY_COLORS: Record<string, string> = {
  coins: 'gold',
  gems: 'purple',
  match_tokens: 'blue',
  evo_tokens: 'green',
  cards: 'orange',
  bundle: 'cyan',
  starter_pack: 'magenta',
  special_offer: 'red',
};

const CATEGORY_OPTIONS = [
  { text: 'Coins', value: 'coins' },
  { text: 'Gems', value: 'gems' },
  { text: 'Match Tokens', value: 'match_tokens' },
  { text: 'Evo Tokens', value: 'evo_tokens' },
  { text: 'Cards', value: 'cards' },
  { text: 'Bundle', value: 'bundle' },
  { text: 'Starter Pack', value: 'starter_pack' },
  { text: 'Special Offer', value: 'special_offer' },
];

const TYPE_OPTIONS = [
  { text: 'Consumable', value: 'consumable' },
  { text: 'Non-Consumable', value: 'non_consumable' },
  { text: 'Subscription', value: 'subscription' },
];

export const ProductList: React.FC<IResourceComponentsProps> = () => {
  const screens = useBreakpoint();
  const [searchText, setSearchText] = useState('');

  const { tableProps, setFilters } = useTable({
    syncWithLocation: true,
  });

  const handleSearch = (value: string) => {
    setSearchText(value);
    const filters: CrudFilters = [];
    if (value) {
      filters.push({ field: 'search', operator: 'eq', value });
    }
    setFilters(filters, 'replace');
  };

  return (
    <List>
      <Row style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Input.Search
            placeholder="Search by name or product ID..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={handleSearch}
          />
        </Col>
      </Row>
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 900 }}
        size={screens.xs ? 'small' : 'middle'}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          showTotal: (total) => `${total} products`,
        }}
      >
        <Table.Column dataIndex="name" title="Name" width={140} ellipsis sorter />
        <Table.Column
          dataIndex="storeProductId"
          title="Product ID"
          width={160}
          ellipsis
          render={(v) => <code style={{ fontSize: 11 }}>{v}</code>}
        />
        <Table.Column
          dataIndex="category"
          title="Category"
          width={120}
          filters={CATEGORY_OPTIONS}
          filterMultiple={false}
          render={(v) => <Tag color={CATEGORY_COLORS[v] || 'default'}>{v}</Tag>}
        />
        <Table.Column
          dataIndex="type"
          title="Type"
          width={110}
          filters={TYPE_OPTIONS}
          filterMultiple={false}
          render={(v) => <Tag>{v}</Tag>}
        />
        <Table.Column
          dataIndex="price"
          title="Price"
          width={100}
          sorter
          render={(v) => (v ? `$${v.usd} / ₹${v.inr}` : '-')}
        />
        <Table.Column
          dataIndex="iosPrice"
          title="iOS Price"
          width={110}
          responsive={['lg']}
          render={(v) =>
            v ? describeIosPrice(v) : <span style={{ color: '#999' }}>Same as Android</span>
          }
        />
        <Table.Column
          dataIndex="items"
          title="Items"
          width={130}
          responsive={['lg']}
          render={(v) => {
            if (!v) return '-';
            const parts: string[] = [];
            if (v.coins) parts.push(`${v.coins} coins`);
            if (v.matchTokens) parts.push(`${v.matchTokens} tokens`);
            if (v.evoTokens) parts.push(`${v.evoTokens} evo`);
            if (v.gems) parts.push(`${v.gems} gems`);
            if (v.cards) parts.push(`${v.cards} cards`);
            return parts.join(', ') || '-';
          }}
        />
        <Table.Column
          dataIndex="isActive"
          title="Status"
          width={80}
          filters={[
            { text: 'Active', value: 'true' },
            { text: 'Inactive', value: 'false' },
          ]}
          filterMultiple={false}
          render={(v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>}
        />
        <Table.Column
          dataIndex="isFeatured"
          title="Featured"
          width={80}
          responsive={['md']}
          filters={[
            { text: 'Yes', value: 'true' },
            { text: 'No', value: 'false' },
          ]}
          filterMultiple={false}
          render={(v) => (v ? <Tag color="blue">Yes</Tag> : '-')}
        />
        <Table.Column
          dataIndex="bestValue"
          title="Best Value"
          width={85}
          responsive={['md']}
          render={(v) => (v ? <Tag color="gold">Yes</Tag> : '-')}
        />
        <Table.Column dataIndex="sortOrder" title="Order" width={70} responsive={['lg']} sorter />
        <Table.Column
          title=""
          dataIndex="actions"
          width={100}
          fixed="right"
          render={(_, record: BaseRecord) => (
            <Space size={4}>
              <EditButton hideText size="small" recordItemId={record.id} />
              <ShowButton hideText size="small" recordItemId={record.id} />
              {screens.sm && <DeleteButton hideText size="small" recordItemId={record.id} />}
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
